import React, {
    Context,
    useCallback,
    useEffect,
    useState,
    ReactNode,
} from 'react';
import { ApolloQueryResult, useApolloClient, useQuery } from '@apollo/client';

import {
    getAllListPages,
    locale,
    getWebpackEnvVar,
    removeNulls,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useStateWithCallback } from '@trqk/hooks';
import { useToast, useTokenModal, usePaymentPlanStatus } from '..';
// import { makeMockDetectionTracks } from '../../components/modals/ManageDetections/helpers';
import { getDetectionStats } from './helpers';

const { GET_SESSION_CURRENT_USER, GET_TRQK_SONG } = queryStrings;

interface DetectionTracksProviderProps {
    children: ReactNode;
    DetectionTracksContext: Context<DetectionTracksProviderValue>;
}

/**
 * Provides a list of DetectionTrack objects for rendering in the DetectionTracksTable
 * within the ManageDetections modal, as well as some other methods and data.
 *
 * @remarks
 * Note that the DetectionTracksProvider must be nested within the PaymentPlanStatusProvider, as it
 * depends on the usePaymentPlanStatus hook to calculate the detectionStats.
 * @param props - {@link DetectionTracksProviderProps}
 * @returns JSX
 */
export const DetectionTracksProvider = ({
    children,
    DetectionTracksContext,
}: DetectionTracksProviderProps): JSX.Element => {
    const { add: addToast } = useToast();
    const { getToken } = useTokenModal();

    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const [didLoadDataForUserId, setDidLoadDataForUserId] =
        useState<Maybe<string>>(null);
    const [refresh, setRefresh] = useState<Maybe<number>>(null);
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;
    const { subscriptionSettings } = usePaymentPlanStatus();
    const detectionCapacity =
        subscriptionSettings?.subscription_detection_tracks ?? 0;
    const planType = subscriptionSettings?.subscription_plan_type ?? null;

    const [detectionStats, setDetectionStats] = useState<DetectionStats>({
        detectionTracksCount: 0,
        detectionCapacity: null,
        enabled: {
            all: null,
            nonDraftOnly: null,
        },
        planType: null,
    });

    // Using a memoized callback prevents a stack overflow in `useStateWithCallback`.
    const onSetDetectionTracks = useCallback(
        (prevState: DetectionTrack[]): void => {
            setDidLoadDataForUserId(userId);
            setRefresh(Date.now());

            const nextDetectionStats = getDetectionStats({
                detectionTracks: prevState,
                detectionCapacity,
                planType,
            });

            setDetectionStats(nextDetectionStats);
        },
        [userId, detectionCapacity, planType],
    );

    // The standard function returned by `React.useState` is not synchronous.
    // `useStateWithCallback` allows us to ensure that the `loading` state is set *after* the
    // `detectionTracks` state is set.
    const [detectionTracks, setDetectionTracks] = useStateWithCallback<
        DetectionTrack[]
    >([], onSetDetectionTracks);

    const client = useApolloClient();

    /**
     * Sets up an array of Promises, one for each trqkAudio that has a `song_id`. Each
     * Promise will do a `getTrqkSong` query and then add trqkSong data to the appropriate
     * item in DetectionTracks.
     *
     * @remarks
     * Note that we can't use Apollo Client's `useLazyQuery` hook for this because it is
     * blocking; i.e. it doesn't start the next query until the current one finishes.
     */
    const addSongDataToDetectionTracks = useCallback(
        ({ items, predicate }: AddSongDataToDetectionTracksInput): void => {
            const getTrqkSongPromises = items.map(
                (item: TrqkSongIdItem): Promise<void> =>
                    new Promise<void>((resolve, reject) => {
                        client
                            .query<{ getTrqkSong: TrqkSong }>({
                                query: GET_TRQK_SONG,
                                variables: {
                                    id: item.trqkSongId,
                                },
                            })
                            .then(
                                (
                                    result: ApolloQueryResult<{
                                        getTrqkSong: TrqkSong;
                                    }>,
                                ): void => {
                                    const trqkSong =
                                        result?.data?.getTrqkSong ?? null;

                                    if (trqkSong === null) {
                                        throw new Error(
                                            `Unable to fetch trqkSong for song_id: ${item.trqkSongId}`,
                                        );
                                    }

                                    // When a trqkSong is fetched, map its fields
                                    //  onto any corresponding detectionTrack
                                    setDetectionTracks(
                                        (
                                            prev: DetectionTrack[],
                                        ): DetectionTrack[] =>
                                            prev.map(
                                                (
                                                    detectionTrack: DetectionTrack,
                                                ): DetectionTrack => {
                                                    if (
                                                        predicate(
                                                            item,
                                                            detectionTrack,
                                                        )
                                                    ) {
                                                        const {
                                                            percent_shares,
                                                            id,
                                                            title,
                                                            work_ids,
                                                        } = trqkSong;

                                                        return {
                                                            ...detectionTrack,
                                                            percent_shares,
                                                            song_id: id,
                                                            title:
                                                                title ?? null,
                                                            work_ids:
                                                                work_ids ??
                                                                null,
                                                        };
                                                    }

                                                    return detectionTrack;
                                                },
                                            ),
                                    );

                                    resolve(undefined);
                                },
                            )
                            .catch((error: Error): void => {
                                reject(error);
                            });
                    }),
            );

            // Call all `getTrqkSong` queries in parallel
            Promise.allSettled(getTrqkSongPromises).then(
                (results: PromiseSettledResult<void>[]): void => {
                    // If any of these queries fails, show a single toast
                    if (
                        results.some(
                            (result: PromiseSettledResult<void>): boolean =>
                                result.status === 'rejected',
                        )
                    ) {
                        addToast({
                            kind: 'warning-banner',
                            content:
                                locale.enUS.DETECTIONS.ERRORS
                                    .FETCH_TRQK_SONG_ERROR,
                        });
                    }
                },
            );
        },
        [addToast, getToken],
    );

    // Fetch all trqkAudios for user and fetch associated trqkSongs as well
    const fetchDetectionTracks = useCallback(async () => {
        let trqkAudios: TrqkAudio[] = [];

        try {
            trqkAudios = await getAllListPages<
                TrqkAudio,
                TableTrqkAudioFilterInput
            >({
                getToken,
                query: `
                    query listTrqkAudiosDetection(
                        $filter: TableTrqkAudioFilterInput
                        $nextToken: String
                        $limit: Int
                    ) {
                        listTrqkAudios(filter: $filter, nextToken: $nextToken, limit: $limit) {
                            items {
                                id
                                asset_url
                                deleted
                                detection_history {
                                    timestamp
                                    status
                                    source
                                }
                                detection_status
                                filename
                                song_id
                            }
                            nextToken
                        }
                    }
                `,
                queryName: 'listTrqkAudios',
                trqkDirectoryUrl,
            });
        } catch {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.DETECTIONS.ERRORS.FETCH_DETECTIONS_ERROR,
            });
        }

        const trqkSongIds: string[] = [];

        const nextDetectionTracks = trqkAudios.map(
            ({
                id,
                asset_url,
                deleted,
                detection_history,
                detection_status,
                filename,
                song_id,
                user_id,
            }: TrqkAudio): DetectionTrack => {
                if (song_id) {
                    trqkSongIds.push(song_id);
                }

                return {
                    id,
                    asset_url,
                    audio_id: id,
                    deleted,
                    detection_history:
                        removeNulls<DetectionStatusChange>(detection_history),
                    detection_status,
                    filename,
                    percent_shares: null,
                    song_id,
                    title: null,
                    work_ids: null,
                    user_id,
                };
            },
        );

        setDetectionTracks(nextDetectionTracks);
        // To test table rendering with large datasets, comment out the line above and uncomment
        // the line below
        // setDetectionTracks(makeMockDetectionTracks(20_000));

        addSongDataToDetectionTracks({
            items: trqkSongIds.map(
                (trqkSongId: string): TrqkSongIdItem => ({ trqkSongId }),
            ),
            predicate: (
                item: TrqkSongIdItem,
                detectionTrack: DetectionTrack,
            ): boolean => detectionTrack.song_id === item.trqkSongId,
        });
    }, [addToast, getToken, addSongDataToDetectionTracks]);

    useEffect(() => {
        if (userId !== null && userId !== didLoadDataForUserId) {
            fetchDetectionTracks();
        }
    }, [didLoadDataForUserId, userId]);

    const loading = didLoadDataForUserId !== userId;

    return (
        <DetectionTracksContext.Provider
            value={{
                addSongDataToDetectionTracks,
                detectionTracks,
                detectionStats,
                loading,
                refresh,
                setDetectionTracks,
            }}
        >
            {children}
        </DetectionTracksContext.Provider>
    );
};
